import React, { useState, useRef, useEffect } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  Upload,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileSpreadsheet
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { useWebSocket } from '@crm-src/hooks/useWebSocket'
import type { ImportProgress } from '@crm-src/lib/websocket'
import { parseCsvToJson } from './csv-parse'

interface ImportDialogProps {
  isOpen: boolean
  onClose: () => void
  entityDisplayName: string
  apiService: {
    import: (data: any[]) => Promise<any>
  }
  onImportComplete?: () => void
}

interface ImportResult {
  imported: number
  total: number
  contacts_added?: number
  processed?: number
  errors: string[]
  warnings?: string[]
  success_rate: number
}

export function CsvImportDialog({ 
  isOpen, 
  onClose, 
  entityDisplayName,
  apiService,
  onImportComplete 
}: ImportDialogProps) {
  const [isImporting, setIsImporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [importProgress, setImportProgress] = useState<number>(0)
  const [importStatus, setImportStatus] = useState<string>('')
  const [jobId, setJobId] = useState<string | null>(null)
  
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { isConnected, onImportProgress } = useWebSocket()

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!isOpen) {
      setImportResult(null)
      setSelectedFile(null)
      setImportProgress(0)
      setImportStatus('')
      setJobId(null)
    }
  }, [isOpen])

  // Listen for import progress updates via WebSocket
  useEffect(() => {
    if (!isConnected || !isOpen) return  // Only listen when dialog is open and connected

    const unsubscribe = onImportProgress((progress: ImportProgress) => {
      
      setImportProgress(progress.progress)
      setImportStatus(progress.message)
      
      if (progress.status === 'completed') {
        setImportResult({
          imported: progress.imported,
          total: progress.total,
          contacts_added: progress.contacts_added || 0,
          processed: progress.processed || progress.total,
          errors: progress.errors || [],
          warnings: progress.warnings || [],
          success_rate: progress.success_rate || 0
        })
        setIsImporting(false)
        setJobId(null)
        
        if (progress.imported > 0 && onImportComplete) {
          onImportComplete()
        }
        
        // Clear selected file after successful import
        if (progress.imported > 0) {
          setSelectedFile(null)
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }
      } else if (progress.status === 'failed') {
        setImportResult({
          imported: 0,
          total: progress.total,
          errors: progress.errors || ['Import failed'],
          success_rate: 0
        })
        setIsImporting(false)
        setJobId(null)
        setImportStatus('Import failed')
      }
    })

    return () => {
      unsubscribe()
    }
  }, [isConnected, onImportProgress, isOpen, onImportComplete]) // Listen when dialog is open and connected

  // Clear file input after successful import
  const resetImport = () => {
    setImportResult(null)
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      // Clear any previous results
      setImportResult(null)
    }
  }


  const parseExcelToJson = async (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          const workbook = XLSX.read(data, { type: 'array' })
          
          // Get the first worksheet
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          
          // Convert worksheet to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1, // Use first row as header
            defval: '' // Default value for empty cells
          }) as any[][]
          
          
          if (jsonData.length < 2) {
            resolve([])
            return
          }
          
          // Detect where actual headers are located
          // Check if the first row looks like a title (mostly empty cells or single value)
          let headerRowIndex = 0
          const firstRow = jsonData[0]
          const nonEmptyInFirstRow = firstRow.filter(cell => cell !== null && cell !== undefined && String(cell).trim() !== '').length
          
          // If first row has very few non-empty cells compared to columns, it's likely a title row
          // Also check if second row has more meaningful headers
          if (jsonData.length > 1) {
            const secondRow = jsonData[1]
            const nonEmptyInSecondRow = secondRow.filter(cell => cell !== null && cell !== undefined && String(cell).trim() !== '').length
            
            // If second row has significantly more non-empty cells, use it as headers
            // Also check if second row contains typical header keywords
            const hasHeaderKeywords = secondRow.some(cell => {
              const cellStr = String(cell || '').toLowerCase()
              return ['id', 'name', 'email', 'phone', 'company', 'customer', 'first_name', 'last_name'].some(keyword => 
                cellStr.includes(keyword)
              )
            })
            
            if ((nonEmptyInFirstRow <= 3 && nonEmptyInSecondRow > nonEmptyInFirstRow) || hasHeaderKeywords) {
              headerRowIndex = 1
            }
          }
          
          // Extract headers from the detected header row
          const headers = jsonData[headerRowIndex].map(h => String(h || '').trim())
          
          // Convert data rows to objects (starting from row after headers)
          const excelData = []
          for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
            const row = jsonData[i]
            if (row.some(cell => cell !== null && cell !== undefined && String(cell).trim() !== '')) {
              const rowObj: any = {}
              headers.forEach((header, index) => {
                const value = row[index]
                rowObj[header] = value !== null && value !== undefined ? String(value).trim() : ''
              })
              excelData.push(rowObj)
              if (i <= headerRowIndex + 3) {
              }
            }
          }
          
          resolve(excelData)
        } catch (error) {
          console.error('Error parsing Excel file:', error)
          reject(error)
        }
      }
      
      reader.onerror = () => {
        reject(new Error('Failed to read Excel file'))
      }
      
      reader.readAsArrayBuffer(file)
    })
  }

  const handleImport = async () => {
    if (!selectedFile) {
      alert('Please select a CSV or Excel file to import')
      return
    }

    try {
      let dataToImport: any[] = []
      
      setImportStatus('Reading file...')
      setImportProgress(10)
      
      // Determine file type and parse accordingly
      const fileName = selectedFile.name.toLowerCase()
      const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls') || 
                     selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                     selectedFile.type === 'application/vnd.ms-excel'
      
      if (isExcel) {
        // Parse Excel file
        dataToImport = await parseExcelToJson(selectedFile)
      } else {
        // Parse CSV file
        const content = await new Promise<string>((resolve) => {
          const reader = new FileReader()
          reader.onload = (e) => resolve(e.target?.result as string)
          reader.readAsText(selectedFile)
        })
        dataToImport = parseCsvToJson(content)
      }
      
      if (dataToImport.length === 0) {
        alert('No valid data found in file')
        return
      }

      setIsImporting(true)
      setImportProgress(20)
      
      
      // Check if WebSocket is connected for async import
      if (!isConnected) {
        alert('WebSocket connection is not established. Please refresh the page and try again.')
        setIsImporting(false)
        return
      }
      
      setImportStatus(`Queueing import of ${dataToImport.length} records...`)
      setImportProgress(30)
      
      // Send all data to the API for background processing
      const response = await apiService.import(dataToImport)
      
      // Check if job was queued successfully (response.status exists for queued jobs)
      if (response.status === 'queued') {
        const importJobId = response.job_id
        
        setJobId(importJobId)
        setImportStatus('Import job queued. Processing will begin shortly...')
        setImportProgress(40)
        
        // WebSocket listener will handle further updates
      } else {
        // Fallback to synchronous response if available
        
        setImportProgress(100)
        setImportStatus('Import complete!')
        
        setImportResult(response)
        
        if (response.imported > 0 && onImportComplete) {
          onImportComplete()
        }
        
        // Clear selected file after successful import
        if (response.imported > 0) {
          setSelectedFile(null)
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }
      }
      
    } catch (error: any) {
      console.error('Import failed:', error)
      setImportResult({
        imported: 0,
        total: 0,
        errors: [error.response?.data?.message || error.message || 'Import failed'],
        success_rate: 0
      })
      setImportStatus('Import failed')
      setIsImporting(false)
    }
  }

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Upload size={20} />
        Import {entityDisplayName}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ maxHeight: 600, pr: 2, overflowY: "auto" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {importResult && importResult.imported > 0 ? (
              // Success View - Show success message with details
              <Box sx={{ py: 4 }}>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Box sx={{ mx: "auto", display: "flex", height: 64, width: 64, alignItems: "center", justifyContent: "center", borderRadius: "50%", bgcolor: (t) => alpha(t.palette.success.main, 0.12), color: "success.main", mb: 2 }}>
                    <CheckCircle size={32} />
                  </Box>
                  <Typography component="h3" sx={{ fontSize: "1.25rem", fontWeight: 600, color: "text.primary", mb: 1 }}>
                    Import Successful!
                  </Typography>
                  <Typography sx={{ fontSize: "1.125rem", color: "text.secondary", mb: 1 }}>
                    <strong>{importResult.imported}</strong> companies imported successfully
                    {importResult.contacts_added && importResult.contacts_added > 0 && (
                      <span>, <strong>{importResult.contacts_added}</strong> additional contacts attached to existing companies</span>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "text.disabled", mb: 2 }}>
                    ({importResult.processed || importResult.total} of {importResult.total} records processed)
                  </Typography>
                </Box>

                {/* Show errors if any */}
                {importResult.errors && importResult.errors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 2 }} icon={<AlertCircle size={16} />}>
                    <AlertTitle>Failed Records ({importResult.total - importResult.imported})</AlertTitle>
                    <Box sx={{ mt: 1, maxHeight: 128, overflowY: "auto" }}>
                      {importResult.errors.slice(0, 5).map((error, index) => (
                        <Typography key={index} sx={{ fontSize: 14, mb: 0.5 }}>
                          • {error}
                        </Typography>
                      ))}
                      {importResult.errors.length > 5 && (
                        <Typography sx={{ fontSize: 14, mt: 1, fontWeight: 500 }}>
                          ... and {importResult.errors.length - 5} more errors
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                )}

                {/* Show warnings if any */}
                {importResult.warnings && importResult.warnings.length > 0 && (
                  <Alert severity="info" sx={{ mb: 2 }} icon={<AlertCircle size={16} />}>
                    <AlertTitle>Warnings ({importResult.warnings.length})</AlertTitle>
                    <Box sx={{ mt: 1, maxHeight: 128, overflowY: "auto" }}>
                      {importResult.warnings.slice(0, 5).map((warning, index) => (
                        <Typography key={index} sx={{ fontSize: 14, mb: 0.5 }}>
                          • {warning}
                        </Typography>
                      ))}
                      {importResult.warnings.length > 5 && (
                        <Typography sx={{ fontSize: 14, mt: 1, fontWeight: 500 }}>
                          ... and {importResult.warnings.length - 5} more warnings
                        </Typography>
                      )}
                    </Box>
                  </Alert>
                )}
              </Box>
            ) : isImporting ? (
              // Progress View - Show import progress
              <Box sx={{ py: 4 }}>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Box sx={{ mx: "auto", display: "flex", height: 64, width: 64, alignItems: "center", justifyContent: "center", borderRadius: "50%", bgcolor: (t) => alpha(t.palette.primary.main, 0.12), mb: 2 }}>
                    <CircularProgress size={32} sx={{ color: "primary.main" }} />
                  </Box>
                  <Typography component="h3" sx={{ fontSize: "1.25rem", fontWeight: 600, color: "text.primary", mb: 1 }}>
                    Processing Import
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    {importStatus || 'Processing your data...'}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 14, mb: 1 }}>
                      <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>Progress</Typography>
                      <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>{importProgress}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={importProgress} sx={{ height: 12, borderRadius: 1 }} />
                  </Box>

                  {selectedFile && (
                    <Box sx={{ bgcolor: "surfaceSoft", borderRadius: 1, p: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ color: "text.disabled", display: "flex" }}><FileSpreadsheet size={16} /></Box>
                        <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                          Processing: {selectedFile.name}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Alert severity="info" icon={<AlertCircle size={16} />}>
                    Please keep this window open while the import is processing.
                    You'll be notified when the import is complete.
                  </Alert>
                </Box>
              </Box>
            ) : (
              // Upload View - Show file input form
              <Box>
                <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 600, mb: 1.5 }}>Upload CSV or Excel File</Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box>
                    <Typography component="label" htmlFor="file-upload" sx={{ fontSize: 14, fontWeight: 500 }}>Select CSV or Excel File</Typography>
                    <TextField
                      id="file-upload"
                      type="file"
                      size="small"
                      fullWidth
                      inputRef={fileInputRef}
                      onChange={handleFileSelect as any}
                      inputProps={{ accept: ".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" }}
                      sx={{ mt: 0.5 }}
                    />
                    {selectedFile && (
                      <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
                        Selected: {selectedFile.name}
                      </Typography>
                    )}
                  </Box>

                  <Alert severity="info" icon={<FileSpreadsheet size={16} />}>
                    <strong>File Format Requirements:</strong>
                    <Box component="ul" sx={{ listStyle: "disc", listStylePosition: "inside", mt: 0.5, fontSize: 14, pl: 0 }}>
                      <li>Supported formats: CSV, Excel (.xlsx, .xls)</li>
                      <li>First row should contain column headers</li>
                      <li>For CSV: Use comma (,) as field separator</li>
                      <li>Required fields: first_name, last_name, email</li>
                      <li>Optional fields: company_name, phone, mobile, etc.</li>
                    </Box>
                  </Alert>

                  <Button
                    onClick={handleImport}
                    disabled={isImporting || !selectedFile}
                    variant="contained"
                    fullWidth
                    startIcon={isImporting ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
                  >
                    {isImporting ? "Importing..." : `Import ${entityDisplayName}`}
                  </Button>

                  {importResult && importResult.imported === 0 && (
                    <Card>
                      <CardHeader
                        title={
                          <Typography component="span" sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}>
                            <Box sx={{ color: "error.main", display: "flex" }}><XCircle size={20} /></Box>
                            Import Failed
                          </Typography>
                        }
                      />
                      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                          {importResult.total} records processed, {importResult.imported} imported successfully
                        </Typography>

                        {importResult.errors.length > 0 && (
                          <Box>
                            <Typography component="label" sx={{ fontSize: 14, fontWeight: 500, color: "error.main" }}>Errors:</Typography>
                            <Box sx={{ height: 128, mt: 0.5, overflowY: "auto" }}>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {importResult.errors.map((error, index) => (
                                  <Alert key={index} severity="error" sx={{ "& .MuiAlert-message": { fontSize: 12 } }}>
                                    {error}
                                  </Alert>
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
